<template>
  <div class="hilo-game">
    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-hilo" :size="18" /> {{ $t('games.hilo.title') }}
        <span class="game-tag">{{ $t('games.hilo.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button
          class="icon-btn"
          :class="{ active: soundEnabled }"
          :title="$t('games.toggle_sound')"
          @click="soundEnabled = !soundEnabled"
        >
          <AppIcon :name="soundEnabled ? 'sound-on' : 'sound-off'" :size="16" />
        </button>
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas Area -->
      <div class="game-canvas-area">
        <div class="canvas-bg-grid"></div>
        <div class="canvas-bg-glow"></div>

        <!-- Result Overlay -->
        <div
          class="result-overlay"
          :class="{ active: gamePhase === 'lost' || gamePhase === 'cashed_out' }"
        >
          <template v-if="gamePhase === 'lost'">
            <div class="result-text loss"><AppIcon name="bust" :size="24" /> {{ $t('games.hilo.bust') }}</div>
            <div class="result-amount loss">
              -${{ betAmount.toFixed(2) }}
            </div>
            <div v-if="lastNextCard" class="result-card-reveal">
              <span class="reveal-label">{{ $t('games.hilo.next_card_was') }}</span>
              <div class="playing-card sm" :class="cardColorClass(lastNextCard)">
                <div class="card-rank-top">{{ rankLabel(lastNextCard.rank) }}</div>
                <div class="card-suit-center">{{ suitSymbol(lastNextCard.suit) }}</div>
              </div>
            </div>
          </template>
          <template v-else-if="gamePhase === 'cashed_out'">
            <div class="result-text win"><AppIcon name="cashout" :size="24" /> {{ $t('games.cashed_out') }}</div>
            <div class="result-amount win">+${{ (lastPayout - betAmount).toFixed(2) }}</div>
            <div class="result-mult">{{ lastMultiplier.toFixed(4) }}×</div>
          </template>
          <button class="btn-new-game" @click="resetGame">{{ $t('games.new_game') }}</button>
        </div>

        <!-- Card Display Area -->
        <div class="hilo-display">
          <!-- Previous card + current card -->
          <div class="hilo-cards">
            <!-- Previous card (face down when no history, face up after first guess) -->
            <div
              v-if="previousCard"
              class="playing-card previous"
              :class="cardColorClass(previousCard)"
            >
              <div class="card-rank-top">{{ rankLabel(previousCard.rank) }}</div>
              <div class="card-suit-center">{{ suitSymbol(previousCard.suit) }}</div>
              <div class="card-rank-bot">{{ rankLabel(previousCard.rank) }}</div>
            </div>
            <div v-else class="playing-card face-down">
              <div class="card-suit-center face-down-symbol">🂠</div>
            </div>

            <!-- Current card (the one to guess on) -->
            <div
              v-if="currentCard"
              class="playing-card current"
              :class="[cardColorClass(currentCard), { 'card-flip': cardFlipping }, guessResultClass]"
            >
              <div class="card-rank-top">{{ rankLabel(currentCard.rank) }}</div>
              <div class="card-suit-center">{{ suitSymbol(currentCard.suit) }}</div>
              <div class="card-rank-bot">{{ rankLabel(currentCard.rank) }}</div>
            </div>
            <div v-else class="playing-card face-down">
              <div class="card-suit-center face-down-symbol">🂠</div>
            </div>
          </div>

          <!-- Running multiplier (shown after at least one correct guess) -->
          <div v-if="gamePhase === 'active' && currentMultiplier > 0" class="hilo-mult-badge">
            <span class="mult-label">{{ $t('games.multiplier') }}</span>
            <span class="mult-value">{{ currentMultiplier.toFixed(4) }}×</span>
          </div>

          <!-- Probability buttons (shown during active game) -->
          <div v-if="gamePhase === 'active' && currentCard" class="hilo-guess-buttons">
            <button
              class="guess-btn higher"
              :class="probColorClass(probabilities.higher)"
              :disabled="loading"
              @click="makeGuess('higher')"
            >
              <span class="guess-icon">⬆</span>
              <span class="guess-label">{{ $t('games.hilo.higher') }}</span>
              <span class="guess-prob">{{ (probabilities.higher * 100).toFixed(1) }}%</span>
            </button>

            <button
              v-if="sameEnabled"
              class="guess-btn same"
              :class="probColorClass(probabilities.same)"
              :disabled="loading"
              @click="makeGuess('same')"
            >
              <span class="guess-icon">=</span>
              <span class="guess-label">{{ $t('games.hilo.same') }}</span>
              <span class="guess-prob">{{ (probabilities.same * 100).toFixed(1) }}%</span>
            </button>

            <button
              class="guess-btn lower"
              :class="probColorClass(probabilities.lower)"
              :disabled="loading"
              @click="makeGuess('lower')"
            >
              <span class="guess-icon">⬇</span>
              <span class="guess-label">{{ $t('games.hilo.lower') }}</span>
              <span class="guess-prob">{{ (probabilities.lower * 100).toFixed(1) }}%</span>
            </button>
          </div>

          <!-- Card history trail strip (last 8 guesses) -->
          <div v-if="recentGuesses.length > 0" class="card-trail">
            <div
              v-for="(entry, idx) in recentGuesses"
              :key="idx"
              class="trail-pip"
              :class="entry.correct ? 'trail-correct' : 'trail-wrong'"
              :title="`${rankLabel(entry.card.rank)}${suitSymbol(entry.card.suit)} → ${entry.guess}: ${entry.correct ? 'correct' : 'wrong'}`"
            >
              <span class="trail-rank" :class="'suit-' + entry.card.suit">
                {{ rankLabel(entry.card.rank) }}{{ suitSymbol(entry.card.suit) }}
              </span>
              <span class="trail-indicator">{{ entry.correct ? '✓' : '✗' }}</span>
            </div>
          </div>

          <!-- Probability sum validation indicator (dev aid, hidden in prod) -->
          <div v-if="gamePhase === 'active'" class="prob-sum-row">
            <span
              class="prob-sum-label"
              :class="{ 'sum-ok': Math.abs(probSum - 1) < 0.001 }"
            >
              {{ (probSum * 100).toFixed(1) }}% covered
            </span>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            @click="activeTab = 'bet'"
          >{{ $t('games.bet_tab') }}</button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'history' }"
            @click="activeTab = 'history'"
          >{{ $t('games.history_title') }}</button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'rules' }"
            @click="activeTab = 'rules'"
          >{{ $t('games.rules_tab') }}</button>
        </div>

        <!-- BET TAB -->
        <div v-show="activeTab === 'bet'" class="game-tab-content">
          <!-- Bet Amount -->
          <label class="ctrl-label">{{ $t('games.bet_amount') }}</label>
          <div class="bet-input-wrap">
            <span class="bet-currency">$</span>
            <input
              class="bet-input"
              type="number"
              v-model.number="betAmount"
              min="0.01"
              step="0.01"
              :disabled="gamePhase === 'active'"
            />
          </div>
          <div class="bet-shortcuts">
            <button class="bet-short" :disabled="gamePhase === 'active'" @click="halveBet">½</button>
            <button class="bet-short" :disabled="gamePhase === 'active'" @click="doubleBet">2×</button>
            <button class="bet-short" :disabled="gamePhase === 'active'" @click="maxBet">Max</button>
          </div>

          <!-- Probability stats (shown before game starts as preview) -->
          <div v-if="gamePhase !== 'active'" class="stat-pills" style="grid-template-columns:1fr 1fr 1fr">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.hilo.higher') }}</div>
              <div class="sp-value">—</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.hilo.same') }}</div>
              <div class="sp-value">—</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.hilo.lower') }}</div>
              <div class="sp-value">—</div>
            </div>
          </div>

          <!-- Live probability stats (during active game) -->
          <div v-if="gamePhase === 'active'" class="stat-pills" style="grid-template-columns:1fr 1fr 1fr">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.hilo.higher') }}</div>
              <div class="sp-value" :class="probValueClass(probabilities.higher)">
                {{ (probabilities.higher * 100).toFixed(1) }}%
              </div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.hilo.same') }}</div>
              <div class="sp-value" :class="probValueClass(probabilities.same)">
                {{ (probabilities.same * 100).toFixed(1) }}%
              </div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.hilo.lower') }}</div>
              <div class="sp-value" :class="probValueClass(probabilities.lower)">
                {{ (probabilities.lower * 100).toFixed(1) }}%
              </div>
            </div>
          </div>

          <!-- Running multiplier + payout stats -->
          <div v-if="gamePhase === 'active'" class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.multiplier') }}</div>
              <div class="sp-value gold">
                {{ currentMultiplier > 0 ? currentMultiplier.toFixed(4) + '×' : '—' }}
              </div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.payout') }}</div>
              <div class="sp-value gold">
                {{ currentMultiplier > 0 ? '$' + currentPayout.toFixed(2) : '—' }}
              </div>
            </div>
          </div>

          <!-- Start Game button (idle/post-game) -->
          <button
            v-if="gamePhase !== 'active'"
            class="btn-action"
            :disabled="loading"
            @click="startGame"
          >
            {{ $t('games.place_bet') }}
          </button>

          <!-- Cashout button (active game, after at least 1 correct guess) -->
          <button
            v-if="gamePhase === 'active'"
            class="btn-cashout"
            :class="{ visible: correctGuessCount > 0 }"
            :disabled="correctGuessCount < 1 || loading"
            @click="doCashout"
          >
            {{ loading ? $t('games.cashing_out') : $t('games.cash_out') + ' $' + currentPayout.toFixed(2) }}
          </button>

          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>

        <!-- HISTORY TAB -->
        <div v-show="activeTab === 'history'" class="game-tab-content">
          <div v-if="guessHistory.length === 0" class="history-empty">
            No guesses yet. Start a game!
          </div>
          <div v-else class="history-strip">
            <div
              v-for="(entry, idx) in guessHistory"
              :key="idx"
              class="history-entry"
              :class="entry.correct ? 'correct' : 'wrong'"
            >
              <div class="he-card">
                <span class="he-rank" :class="'suit-' + entry.card.suit">
                  {{ rankLabel(entry.card.rank) }}{{ suitSymbol(entry.card.suit) }}
                </span>
              </div>
              <div class="he-guess">
                <span class="he-arrow">{{ guessArrow(entry.guess) }}</span>
                <span class="he-result">{{ entry.correct ? '✓' : '✗' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- RULES TAB -->
        <div v-show="activeTab === 'rules'" class="game-tab-content">
          <div class="rules-section">
            <div class="rules-title"><span class="rules-title-icon"><AppIcon name="game-hilo" :size="18" /></span> How to Play</div>
            <ul class="rules-list">
              <li class="rules-item">A card is dealt face-up from a shuffled 52-card deck</li>
              <li class="rules-item">Predict if the next card will be Higher, Lower, or the Same rank</li>
              <li class="rules-item">Correct guesses multiply your running payout</li>
              <li class="rules-item">A wrong guess loses your entire bet</li>
              <li class="rules-item">Cash out at any time after a correct guess to collect winnings</li>
              <li class="rules-item">{{ $t('games.hilo.rule_ace_high') }}</li>
            </ul>
          </div>
          <div class="rules-section">
            <div class="rules-title"><span class="rules-title-icon">📊</span> Probability Colors</div>
            <ul class="rules-list">
              <li class="rules-item"><span class="color-dot green"></span> Green: &gt;50% chance</li>
              <li class="rules-item"><span class="color-dot yellow"></span> Yellow: 25–50% chance</li>
              <li class="rules-item"><span class="color-dot red"></span> Red: &lt;25% chance</li>
            </ul>
          </div>
          <div class="fair-tag"><AppIcon name="provably-fair" :size="12" /> Provably Fair Game</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'

// ---- Types ----

interface Card {
  rank: number
  suit: string
}

interface GuessHistoryEntry {
  position: number
  guess: string
  correct: boolean
  card: Card
}

interface Probabilities {
  higher: number
  lower: number
  same: number
}

type GamePhase = 'idle' | 'active' | 'lost' | 'cashed_out'

// ---- Suit / rank helpers ----

const SUIT_SYMBOLS: Record<string, string> = {
  clubs: '♣',
  diamonds: '♦',
  hearts: '♥',
  spades: '♠',
}

const RED_SUITS = new Set(['diamonds', 'hearts'])

function suitSymbol(suit: string): string {
  return SUIT_SYMBOLS[suit] ?? suit
}

function rankLabel(rank: number): string {
  const labels: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' }
  return labels[rank] ?? String(rank)
}

function cardColorClass(card: Card): string {
  return RED_SUITS.has(card.suit) ? 'red' : 'black'
}

function guessArrow(guess: string): string {
  return guess === 'higher' ? '⬆' : guess === 'lower' ? '⬇' : '='
}

// ---- Prob color helpers ----

function probColorClass(prob: number): string {
  if (prob > 0.5) return 'prob-high'
  if (prob >= 0.25) return 'prob-mid'
  return 'prob-low'
}

function probValueClass(prob: number): string {
  if (prob > 0.5) return 'color-success'
  if (prob >= 0.25) return 'color-warning'
  return 'color-danger'
}

// ---- Stores / router ----

const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const toastStore = useToastStore()

// ---- Game state ----

const gamePhase = ref<GamePhase>('idle')
const gameStateId = ref<number | null>(null)
const currentCard = ref<Card | null>(null)
const previousCard = ref<Card | null>(null)
const probabilities = ref<Probabilities>({ higher: 0, lower: 0, same: 0 })
const guessHistory = ref<GuessHistoryEntry[]>([])
const currentMultiplier = ref(0)
const betAmount = ref(1.0)
const loading = ref(false)
const cardFlipping = ref(false)
const guessResultAnimation = ref<'correct' | 'wrong' | null>(null)
const soundEnabled = ref(true)
const sameEnabled = ref(true)
const activeTab = ref<'bet' | 'history' | 'rules'>('bet')

// Post-game
const lastPayout = ref(0)
const lastMultiplier = ref(0)
const lastNextCard = ref<Card | null>(null)

// ---- Computed ----

const correctGuessCount = computed(() =>
  guessHistory.value.filter(g => g.correct).length
)

const currentPayout = computed(() =>
  currentMultiplier.value > 0 ? betAmount.value * currentMultiplier.value : 0
)

const probSum = computed(() =>
  probabilities.value.higher + probabilities.value.lower + probabilities.value.same
)

const guessResultClass = computed(() => {
  if (guessResultAnimation.value === 'correct') return 'card-pulse-green'
  if (guessResultAnimation.value === 'wrong') return 'card-pulse-red'
  return ''
})

/** Last 8 guesses shown as a horizontal trail strip below the game canvas */
const recentGuesses = computed(() => guessHistory.value.slice(-8))

const formattedBalance = computed(() => {
  const bal = walletStore.balance?.balance
  if (bal == null) return '—'
  return `$${bal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
})

// ---- Bet helpers ----

function halveBet() {
  betAmount.value = Math.max(0.01, +(betAmount.value / 2).toFixed(2))
}

function doubleBet() {
  betAmount.value = +(betAmount.value * 2).toFixed(2)
}

function maxBet() {
  const bal = walletStore.balance?.balance
  if (bal != null && bal > 0) {
    betAmount.value = +bal.toFixed(2)
  }
}

// ---- Reset ----

function resetGame() {
  gamePhase.value = 'idle'
  gameStateId.value = null
  currentCard.value = null
  previousCard.value = null
  probabilities.value = { higher: 0, lower: 0, same: 0 }
  guessHistory.value = []
  currentMultiplier.value = 0
  guessResultAnimation.value = null
  lastPayout.value = 0
  lastMultiplier.value = 0
  lastNextCard.value = null
}

// ---- API: start game ----

interface StartResponse {
  game_state_id: number
  current_card: Card
  current_position: number
  current_multiplier: number
  guess_history: GuessHistoryEntry[]
  probabilities: Probabilities
  bet_amount: number
}

async function startGame() {
  if (loading.value) return

  if (!betAmount.value || betAmount.value <= 0) {
    toastStore.error(t('games.invalid_bet_amount'))
    return
  }
  const bal = walletStore.balance?.balance ?? 0
  if (betAmount.value > bal) {
    toastStore.error(t('games.insufficient_balance'))
    return
  }

  loading.value = true
  try {
    const { data } = await apiClient.post<StartResponse>('/api/games/hilo/start', {
      amount: betAmount.value.toFixed(2),
    })

    gameStateId.value = data.game_state_id
    currentCard.value = data.current_card
    previousCard.value = null
    probabilities.value = data.probabilities
    guessHistory.value = []
    currentMultiplier.value = 0
    gamePhase.value = 'active'

    walletStore.applyGameResult(betAmount.value, 0)
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    toastStore.error(msg ?? 'Failed to start game')
  } finally {
    loading.value = false
  }
}

// ---- API: guess ----

interface GuessResponse {
  correct: boolean
  guess: string
  current_card: Card
  previous_card?: Card
  next_card?: Card
  current_multiplier: number
  current_position: number
  probabilities: Probabilities
  status: string
  guess_history: GuessHistoryEntry[]
}

async function makeGuess(direction: 'higher' | 'lower' | 'same') {
  if (loading.value || gamePhase.value !== 'active') return

  loading.value = true
  cardFlipping.value = true

  try {
    const { data } = await apiClient.post<GuessResponse>('/api/games/hilo/guess', {
      guess: direction,
    })

    guessHistory.value = data.guess_history

    // Short flip delay before updating the card
    await new Promise(resolve => setTimeout(resolve, 200))
    cardFlipping.value = false

    if (data.correct) {
      // Advance to the new card and pulse green
      previousCard.value = data.previous_card ?? null
      currentCard.value = data.current_card
      probabilities.value = data.probabilities
      currentMultiplier.value = data.current_multiplier
      guessResultAnimation.value = 'correct'
      setTimeout(() => { guessResultAnimation.value = null }, 650)
    } else {
      // Wrong guess — pulse red briefly, then show game-over overlay
      lastNextCard.value = data.next_card ?? null
      guessResultAnimation.value = 'wrong'
      await new Promise(resolve => setTimeout(resolve, 350))
      guessResultAnimation.value = null
      gamePhase.value = 'lost'
      currentCard.value = null
      toastStore.error(t('games.hilo.wrong_guess'))
    }
  } catch (err: unknown) {
    cardFlipping.value = false
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    toastStore.error(msg ?? 'Failed to submit guess')
  } finally {
    loading.value = false
  }
}

// ---- API: cashout ----

interface CashoutResponse {
  payout: number
  multiplier: number
  guess_count: number
  status: string
}

async function doCashout() {
  if (loading.value || gamePhase.value !== 'active') return
  if (correctGuessCount.value < 1) return

  loading.value = true
  try {
    const { data } = await apiClient.post<CashoutResponse>('/api/games/hilo/cashout')

    lastPayout.value = data.payout
    lastMultiplier.value = data.multiplier
    gamePhase.value = 'cashed_out'

    walletStore.applyGameResult(0, data.payout)
    toastStore.success(`Cashed out $${data.payout.toFixed(2)}!`)
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    toastStore.error(msg ?? 'Failed to cash out')
  } finally {
    loading.value = false
  }
}

// ---- Resume in-progress game on mount ----

interface StateResponse {
  status: string
  game_state_id?: number
  current_card?: Card
  current_position?: number
  current_multiplier?: number
  cumulative_probability?: number
  guess_history?: GuessHistoryEntry[]
  probabilities?: Probabilities
  bet_amount?: number
}

onMounted(async () => {
  await walletStore.fetchBalance()

  try {
    const { data } = await apiClient.get<StateResponse>('/api/games/hilo/state')
    if (data.status === 'pending' && data.current_card) {
      gameStateId.value = data.game_state_id ?? null
      currentCard.value = data.current_card
      probabilities.value = data.probabilities ?? { higher: 0, lower: 0, same: 0 }
      guessHistory.value = data.guess_history ?? []
      currentMultiplier.value = data.current_multiplier ?? 0
      betAmount.value = data.bet_amount ?? betAmount.value
      gamePhase.value = 'active'

      if (guessHistory.value.length > 0) {
        previousCard.value = guessHistory.value[guessHistory.value.length - 1].card
      }
    }
  } catch {
    // No active game — stay idle
  }
})
</script>

<style scoped>
.hilo-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg-primary, #0f1923);
  color: var(--text-primary, #fff);
  font-family: var(--font-body, 'DM Sans', sans-serif);
}

/* ---- Topbar ---- */
.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255,255,255,.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}
.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
}
.btn-back:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}
.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(0,231,1,.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0,231,1,.2);
}
.game-topbar-actions { margin-left: auto; display: flex; align-items: center; gap: 6px; }
.icon-btn {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  border: none;
  transition: all 150ms ease;
}
.icon-btn:hover, .icon-btn.active { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

/* ---- Body ---- */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ---- Canvas Area ---- */
.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-direction: column;
}
.canvas-bg-grid {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(0,231,1,.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,231,1,.025) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}
.canvas-bg-glow {
  position: absolute; pointer-events: none;
  right: -100px; top: -100px;
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(0,231,1,.05) 0%, transparent 65%);
}

/* ---- Result Overlay ---- */
.result-overlay {
  position: absolute; inset: 0;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: rgba(13,27,42,.9);
  backdrop-filter: blur(4px);
  z-index: 10;
  opacity: 0; pointer-events: none;
  transition: opacity .3s ease;
  gap: 12px;
}
.result-overlay.active { opacity: 1; pointer-events: all; }
.result-text {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 42px;
  animation: winPop .4s ease both;
}
.result-text.win { color: var(--color-success, #00e701); text-shadow: 0 0 40px rgba(0,231,1,.5); }
.result-text.loss { color: var(--color-danger, #ed4245); text-shadow: 0 0 40px rgba(237,66,69,.5); }
.result-amount {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 22px; font-weight: 700;
}
.result-amount.win { color: var(--color-success, #00e701); }
.result-amount.loss { color: var(--color-danger, #ed4245); }
.result-mult {
  font-family: var(--font-mono, monospace);
  font-size: 16px;
  color: var(--accent-gold, #ffd700);
}
.result-card-reveal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.reveal-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: .06em;
}
.btn-new-game {
  margin-top: 8px;
  padding: 12px 28px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px; font-weight: 700;
  border-radius: 8px; border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-new-game:hover { background: var(--accent-primary-hover, #00cc01); transform: translateY(-1px); }

/* ---- Card Display ---- */
.hilo-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  z-index: 1;
  padding: 24px;
}

.hilo-cards {
  display: flex;
  align-items: center;
  gap: 28px;
}

.playing-card {
  width: 110px;
  height: 155px;
  border-radius: 12px;
  background: var(--color-on-dark, #fff);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.55);
  transition: transform .25s ease;
  position: relative;
  user-select: none;
}
.playing-card.previous {
  opacity: 0.65;
  transform: scale(0.88);
}
.playing-card.current {
  box-shadow: 0 8px 32px rgba(0,0,0,.6), 0 0 0 2px rgba(255,255,255,.15);
}
.playing-card.current:hover {
  transform: scale(1.02);
}
.playing-card.sm {
  width: 72px; height: 100px;
  border-radius: 8px;
  padding: 7px;
}
.playing-card.face-down {
  background: linear-gradient(135deg, #1475e1, #0d5abf);
}
.playing-card.red { color: var(--card-red, #e53935); }
.playing-card.black { color: var(--roulette-black-deep, #1a1a1a); }

.card-rank-top {
  font-size: 20px; font-weight: 900;
  font-family: var(--font-display, 'Russo One', sans-serif);
  line-height: 1;
}
.card-suit-center {
  font-size: 44px;
  text-align: center;
  flex: 1;
  display: flex; align-items: center; justify-content: center;
}
.face-down-symbol {
  font-size: 40px;
  color: rgba(255,255,255,.4);
}
.card-rank-bot {
  font-size: 20px; font-weight: 900;
  font-family: var(--font-display, 'Russo One', sans-serif);
  line-height: 1;
  align-self: flex-end;
  transform: rotate(180deg);
}
.playing-card.sm .card-rank-top { font-size: 14px; }
.playing-card.sm .card-suit-center { font-size: 28px; }

/* Card flip animation */
@keyframes cardFlip {
  0%   { transform: scaleX(1); }
  50%  { transform: scaleX(0); }
  100% { transform: scaleX(1); }
}
.card-flip { animation: cardFlip .3s ease; }

/* ---- Running multiplier badge ---- */
.hilo-mult-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.mult-label {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .08em;
  color: var(--text-tertiary, #7f8fa4);
}
.mult-value {
  font-family: var(--font-mono, monospace);
  font-size: 26px; font-weight: 700;
  color: var(--accent-gold, #ffd700);
  text-shadow: 0 0 20px rgba(255,215,0,.4);
}

/* ---- Guess Buttons ---- */
.hilo-guess-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}
.guess-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 90px;
  padding: 14px 10px;
  border-radius: 10px;
  border: 2px solid transparent;
  cursor: pointer;
  font-family: inherit;
  transition: all 150ms ease;
}
.guess-btn:disabled {
  opacity: .4;
  cursor: not-allowed;
}

/* Color variants — high/mid/low probability */
.guess-btn.prob-high {
  background: rgba(0,231,1,.12);
  border-color: rgba(0,231,1,.35);
  color: var(--color-success, #00e701);
}
.guess-btn.prob-high:not(:disabled):hover {
  background: rgba(0,231,1,.22);
  box-shadow: 0 0 18px rgba(0,231,1,.3);
  transform: translateY(-2px);
}
.guess-btn.prob-mid {
  background: rgba(255,167,38,.12);
  border-color: rgba(255,167,38,.35);
  color: var(--color-warning, #ffa726);
}
.guess-btn.prob-mid:not(:disabled):hover {
  background: rgba(255,167,38,.22);
  box-shadow: 0 0 18px rgba(255,167,38,.3);
  transform: translateY(-2px);
}
.guess-btn.prob-low {
  background: rgba(237,66,69,.12);
  border-color: rgba(237,66,69,.35);
  color: var(--color-danger, #ed4245);
}
.guess-btn.prob-low:not(:disabled):hover {
  background: rgba(237,66,69,.22);
  box-shadow: 0 0 18px rgba(237,66,69,.3);
  transform: translateY(-2px);
}

.guess-icon { font-size: 20px; }
.guess-label { font-size: 11px; font-weight: 800; letter-spacing: .05em; }
.guess-prob { font-family: var(--font-mono, monospace); font-size: 13px; font-weight: 700; }

/* ---- Probability sum row ---- */
.prob-sum-row {
  margin-top: -8px;
}
.prob-sum-label {
  font-size: 10px;
  color: var(--text-tertiary, #7f8fa4);
}
.prob-sum-label.sum-ok { color: var(--color-success, #00e701); }

/* ---- Sidebar ---- */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255,255,255,.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}
.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255,255,255,.06);
  flex-shrink: 0;
}
.game-tab {
  flex: 1; padding: 13px 4px;
  font-size: 11px; font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center; cursor: pointer;
  border-bottom: 2px solid transparent;
  border-top: none; border-left: none; border-right: none;
  background: none;
  text-transform: uppercase; letter-spacing: .05em;
  transition: all 150ms ease;
  font-family: inherit;
}
.game-tab.active { color: var(--accent-primary, #00e701); border-bottom-color: var(--accent-primary, #00e701); }
.game-tab:not(.active):hover { color: var(--text-secondary, #b1bad3); }
.game-tab-content {
  flex: 1; overflow-y: auto;
  padding: 14px;
  display: flex; flex-direction: column;
  gap: 0;
}
.game-tab-content::-webkit-scrollbar { width: 3px; }

/* ---- Bet controls ---- */
.ctrl-label {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 6px;
  display: block;
}
.bet-input-wrap {
  position: relative; display: flex; align-items: center;
  margin-bottom: 6px;
}
.bet-currency {
  position: absolute; left: 10px;
  font-size: 13px; color: var(--text-tertiary, #7f8fa4);
  pointer-events: none;
}
.bet-input {
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 8px;
  padding: 10px 10px 10px 24px;
  color: var(--text-primary, #fff);
  font-family: var(--font-mono, monospace);
  font-size: 14px; font-weight: 600;
  width: 100%;
  transition: border-color 150ms ease;
}
.bet-input:focus { outline: none; border-color: rgba(20,117,225,.5); }
.bet-input:disabled { opacity: .5; }
.bet-shortcuts {
  display: flex; gap: 4px; margin-bottom: 14px;
}
.bet-short {
  flex: 1; padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px; font-weight: 700;
  border-radius: 4px; border: none;
  cursor: pointer; transition: all 150ms ease; text-align: center;
  font-family: inherit;
}
.bet-short:hover:not(:disabled) { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.bet-short:disabled { opacity: .4; cursor: not-allowed; }

/* ---- Stat pills ---- */
.stat-pills {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 6px; margin-bottom: 14px;
}
.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px; padding: 8px 10px;
  border: 1px solid rgba(255,255,255,.04);
}
.sp-label {
  font-size: 9px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 3px;
}
.sp-value {
  font-family: var(--font-mono, monospace);
  font-size: 13px; font-weight: 700;
  color: var(--accent-primary, #00e701);
}
.sp-value.gold { color: var(--accent-gold, #ffd700); }

/* Probability value colors in stat pills */
.color-success { color: var(--color-success, #00e701) !important; }
.color-warning { color: var(--color-warning, #ffa726) !important; }
.color-danger  { color: var(--color-danger, #ed4245) !important; }

/* ---- Action buttons ---- */
.btn-action {
  width: 100%; padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000); font-size: 15px; font-weight: 800;
  border-radius: 8px; border: none; cursor: pointer;
  transition: all 150ms ease; letter-spacing: .03em;
  margin-bottom: 8px; font-family: inherit;
}
.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0,231,1,.35);
  transform: translateY(-1px);
}
.btn-action:disabled { opacity: .4; cursor: not-allowed; }

.btn-cashout {
  width: 100%; padding: 14px;
  background: var(--color-warning, #ffa726);
  color: var(--color-on-accent, #000); font-size: 15px; font-weight: 800;
  border-radius: 8px; border: none; cursor: pointer;
  transition: all 150ms ease; letter-spacing: .03em;
  margin-bottom: 8px;
  display: none;
  font-family: inherit;
}
.btn-cashout.visible { display: block; }
.btn-cashout:hover:not(:disabled) {
  background: var(--color-warning, #e09000);
  box-shadow: 0 0 20px rgba(255,167,38,.4);
}
.btn-cashout:disabled { opacity: .4; cursor: not-allowed; }

/* ---- Balance row ---- */
.balance-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255,255,255,.05);
  margin-top: 4px;
}
.bal-label { font-size: 11px; color: var(--text-tertiary, #7f8fa4); }
.bal-value { font-family: var(--font-mono, monospace); font-size: 13px; font-weight: 700; }
.bal-value.green { color: var(--color-success, #00e701); }

/* ---- History tab ---- */
.history-empty {
  font-size: 13px; color: var(--text-tertiary, #7f8fa4);
  text-align: center; padding: 24px 0;
}
.history-strip {
  display: flex; flex-direction: column; gap: 6px;
}
.history-entry {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid transparent;
}
.history-entry.correct { border-color: rgba(0,231,1,.2); }
.history-entry.wrong { border-color: rgba(237,66,69,.2); }
.he-card { min-width: 36px; }
.he-rank {
  font-family: var(--font-mono, monospace);
  font-size: 14px; font-weight: 700;
}
.he-rank.suit-hearts, .he-rank.suit-diamonds { color: var(--card-red, #e53935); }
.he-rank.suit-clubs, .he-rank.suit-spades { color: var(--text-secondary, #b1bad3); }
.he-guess {
  display: flex; align-items: center; gap: 6px;
  font-size: 14px;
}
.he-arrow { font-size: 16px; color: var(--text-secondary, #b1bad3); }
.he-result { font-weight: 700; }
.history-entry.correct .he-result { color: var(--color-success, #00e701); }
.history-entry.wrong .he-result { color: var(--color-danger, #ed4245); }

/* ---- Rules tab ---- */
.rules-section { margin-bottom: 16px; }
.rules-title {
  font-size: 12px; font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 8px;
  display: flex; align-items: center; gap: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255,255,255,.05);
}
.rules-title-icon { font-size: 14px; }
.rules-list { list-style: none; display: flex; flex-direction: column; gap: 5px; }
.rules-item {
  font-size: 12px; color: var(--text-secondary, #b1bad3);
  padding-left: 14px; position: relative; line-height: 1.55;
  display: flex; align-items: center; gap: 6px;
}
.rules-item::before {
  content: '▸'; position: absolute; left: 0;
  color: var(--accent-primary, #00e701); font-size: 10px; top: 1px;
}
.color-dot {
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
}
.color-dot.green { background: var(--color-success, #00e701); }
.color-dot.yellow { background: var(--color-warning, #ffa726); }
.color-dot.red { background: var(--color-danger, #ed4245); }
.fair-tag {
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(20,117,225,.1);
  border: 1px solid rgba(20,117,225,.2);
  border-radius: 9999px; padding: 4px 10px;
  font-size: 10px; font-weight: 700; letter-spacing: .05em;
  color: var(--accent-secondary, #1475e1);
  margin-top: 12px; cursor: pointer;
}
.fair-tag:hover { background: rgba(20,117,225,.18); }

/* ---- Guess result pulse animations ---- */
@keyframes cardPulseGreen {
  0%   { box-shadow: 0 8px 32px rgba(0,0,0,.6), 0 0 0 2px rgba(255,255,255,.15); }
  35%  { box-shadow: 0 8px 32px rgba(0,0,0,.6), 0 0 0 5px rgba(0,231,1,.85), 0 0 36px rgba(0,231,1,.6); }
  100% { box-shadow: 0 8px 32px rgba(0,0,0,.6), 0 0 0 2px rgba(255,255,255,.15); }
}
@keyframes cardPulseRed {
  0%   { box-shadow: 0 8px 32px rgba(0,0,0,.6), 0 0 0 2px rgba(255,255,255,.15); }
  30%  { box-shadow: 0 8px 32px rgba(0,0,0,.6), 0 0 0 5px rgba(237,66,69,.85), 0 0 36px rgba(237,66,69,.6); }
  100% { box-shadow: 0 8px 32px rgba(0,0,0,.6), 0 0 0 2px rgba(255,255,255,.15); }
}
.card-pulse-green { animation: cardPulseGreen 0.65s ease; }
.card-pulse-red   { animation: cardPulseRed   0.4s ease; }

/* ---- Card history trail strip ---- */
.card-trail {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 360px;
  padding: 0 4px;
}
.trail-pip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255,255,255,.07);
  border-radius: 6px;
  padding: 4px 7px;
  min-width: 32px;
  transition: border-color 150ms ease;
}
.trail-pip.trail-correct {
  border-color: rgba(0,231,1,.3);
  background: rgba(0,231,1,.06);
}
.trail-pip.trail-wrong {
  border-color: rgba(237,66,69,.3);
  background: rgba(237,66,69,.06);
}
.trail-rank {
  font-family: var(--font-mono, monospace);
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}
.trail-rank.suit-hearts, .trail-rank.suit-diamonds { color: var(--card-red, #e53935); }
.trail-rank.suit-clubs,  .trail-rank.suit-spades  { color: var(--text-secondary, #b1bad3); }
.trail-indicator {
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
}
.trail-pip.trail-correct .trail-indicator { color: var(--color-success, #00e701); }
.trail-pip.trail-wrong   .trail-indicator { color: var(--color-danger, #ed4245); }

/* ---- Animations ---- */
@keyframes winPop {
  0%   { transform: scale(.8); opacity: 0; }
  60%  { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

/* ---- Responsive ---- */
@media (max-width: 768px) {
  .game-sidebar { display: none; }
  .hilo-guess-buttons { gap: 6px; }
  .guess-btn { width: 72px; padding: 12px 6px; }
  .playing-card { width: 90px; height: 126px; }
  .card-suit-center { font-size: 36px; }
}
</style>
